import i18n from 'i18next';
import { enUS, es, vi, zhCN } from 'date-fns/locale';
import {
  addDays,
  addSeconds,
  differenceInSeconds,
  format,
  formatDistance,
  isValid,
  startOfDay,
  startOfWeek,
} from 'date-fns';
import { Language } from 'i18n/utils';
import { EMPTY_DATA_VALUE } from '../constants';

type DateFnsLocale = keyof typeof locales;

type IntLanguage = Exclude<Language, 'tl'>;

const locales = { enUS, es, vi, zhCN };

const intLangToDateFnsLocale: Record<IntLanguage, DateFnsLocale> = {
  en: 'enUS',
  es: 'es',
  vi: 'vi',
  zh: 'zhCN',
};

export const DATE_ONLY_FORMAT = 'MMM d, yyyy';

export const SHORT_MONTH_DAY_12HOUR_FORMAT = 'MMM d, h:mm a';

export const SHORT_MONTH_DAY_YEAR_12HOUR_FORMAT = 'MMM d, yyyy, h:mm a';

export const getTimeZoneAbbr = (date: Date, timeZone?: string): string => {
  return (
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'short',
    })
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')?.value || ''
  );
};

const getDateFnsLocale = (): (typeof locales)[DateFnsLocale] =>
  locales[intLangToDateFnsLocale[i18n.resolvedLanguage as IntLanguage]];

export const formatDate = (
  date: Date,
  formatStr = 'PP',
  includeTimeZone = false,
): string => {
  const formattedDate = format(date, formatStr, {
    locale: getDateFnsLocale(),
  });
  if (includeTimeZone) {
    return `${formattedDate} ${getTimeZoneAbbr(date)}`;
  }
  return formattedDate;
};

/**
 * The one source of relative-time copy for past dates, e.g. "5 minutes ago".
 * Future dates (e.g. from a server clock running ahead of the device) are
 * clamped to "now" so we never render copy like "in less than a minute".
 */
export const getTimeAgo = (time: string | Date): string => {
  const date = new Date(time);
  const now = new Date();
  return formatDistance(date > now ? now : date, now, {
    addSuffix: true,
    locale: getDateFnsLocale(),
  });
};

export const getDateFormatted = (
  time: string | Date | number,
  options?: {
    includeYear?: boolean;
    includeTimeZone?: boolean;
    includeDayOfWeek?: boolean;
  },
): string => {
  const date = new Date(time);
  const combiner = i18n.t('common.at');
  const dateFormat = options?.includeYear ? 'MMM d, yyyy' : 'MMM d';
  const formatStr = options?.includeDayOfWeek ? `E, ${dateFormat}` : dateFormat;

  const formattedDate = formatDate(date, formatStr);
  const formattedTime = formatDate(date, 'h:mm a');

  if (options?.includeTimeZone) {
    return `${formattedDate} ${combiner} ${formattedTime} ${getTimeZoneAbbr(date)}`;
  }

  return `${formattedDate} ${combiner} ${formattedTime}`;
};

export const getDateFormattedInboxMessage = (time: string): string => {
  const date = new Date(time);
  const combiner = i18n.t('common.at');

  const formattedDate = formatDate(date, 'EEEE, MMM d');
  const formattedTime = formatDate(date, 'h:mm a');

  return `${formattedDate} ${combiner} ${formattedTime}`;
};

export const getDateStringWithNoZeroTimeZone = (): string =>
  new Date().toISOString().slice(0, -1);

/**
 * Formats a Date object into a local ISO 8601 string without timezone information.
 *
 * The function preserves the local date and time components exactly as provided,
 * returning them in the format 'YYYY-MM-DDTHH:mm:ss'. Unlike toISOString(),
 * which converts to UTC and appends 'Z', this maintains the local time representation
 * without any timezone indicators.
 */
export const formatLocalDateTimeWithoutTimezone = (date: Date): string => {
  return formatDate(date, "yyyy-MM-dd'T'HH:mm:ss");
};

/**
 * Converts a date to UTC and formats it using the given date-fns format string.
 */
export const formatLocalDateToUtc = (date: Date, formatStr = 'PP'): string => {
  const utcDate = new Date(
    date.getTime() - date.getTimezoneOffset() * 1000 * 60,
  );
  return formatDate(utcDate, formatStr);
};

export const safeParseDate = (
  dateInput: string | number | Date | null | undefined,
): Date | undefined => {
  if (!dateInput) return undefined;
  if (dateInput instanceof Date) {
    return isValid(dateInput) ? dateInput : undefined;
  }
  const date = new Date(dateInput);
  return isValid(date) ? date : undefined;
};

export const getDayOfWeek = (day: number, formatStr = 'EEEE'): string => {
  if (day < 0 || day > 6) {
    throw new Error('Invalid day number. Must be between 0 and 6.');
  }

  const baseDate = startOfWeek(new Date(), { weekStartsOn: 0 }); // Start of the week (Sunday)
  const targetDate = addDays(baseDate, day);

  return formatDate(targetDate, formatStr);
};

export const formatSecondsSinceMidnight = (seconds: number | null): string => {
  if (Number.isNaN(seconds) || seconds === null) return '';
  const midnight = startOfDay(new Date());
  const dateWithTime = addSeconds(midnight, seconds);
  return formatDate(dateWithTime, 'h:mm a');
};

export const secondsSinceMidnightToDate = (
  seconds: number | null,
): Date | null => {
  if (Number.isNaN(seconds) || seconds === null) return null;
  const midnight = startOfDay(new Date());
  return addSeconds(midnight, seconds);
};

export const dateToSecondsSinceMidnight = (
  date: Date | null,
): number | null => {
  if (!date) return null;
  const midnight = startOfDay(date);
  return differenceInSeconds(date, midnight);
};

export const formatTimestamp = (timestamp: string | number): string => {
  const date = safeParseDate(timestamp);

  if (!date) return EMPTY_DATA_VALUE;

  // If the current year is the same as the year of the date, don't include the year in the formatted date.
  // Example: Fri, Jul 16 at 12:00 PM PDT
  const currentDate = new Date();
  if (currentDate.getFullYear() === date.getFullYear()) {
    return getDateFormatted(date, {
      includeDayOfWeek: true,
      includeTimeZone: true,
    });
  }

  // Example: Fri, Jul 16, 2021 at 12:00 PM PDT
  return getDateFormatted(date, {
    includeYear: true,
    includeDayOfWeek: true,
    includeTimeZone: true,
  });
};
